/// <amd-module name="Environments/EnvironmentDetails/Components/EnvironmentComponent" />
import React = require("react");

import { EnvironmentInstance } from "DistributedTask/Client/Contracts/TaskAgent";

import { addBreadcrumbs } from "Environments/Breadcrumb/EnvironmentsHubBreadcrumbService";
import { EnvironmentActionsCreator } from "Environments/EnvironmentDetails/ActionsCreators/EnvironmentActionsCreator";
import { PageKey } from "Environments/EnvironmentDetails/Common/Utils";
import * as Resources from "Environments/EnvironmentDetails/Resources";
import { EnvironmentStore } from "Environments/EnvironmentDetails/Stores/EnvironmentStore";
import { getDeleteDialog, IDeleteDialogProps } from "Environments/EnvironmentsCommon/DeleteDialog";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getCurrentEnvironmentIdFromRoute, getEnvironmentChecksPageUrl, getEnvironmentPageUrl, getEnvironmentsPageUrl, getEnvironmentsSecurityPageUrl } from "Environments/EnvironmentsCommon/Link";
import { EnvironmentTabKeys, UrlParamKeys } from "Environments/EnvironmentsCommon/Utils";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";

import { IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { EnvironmentChecksComponent } from "Components/EnvironmentChecksComponent";
import { IEnvironmentDeploymentHistoryResult, IEnvironmentObject } from "Environments/Client/EnvironmentContracts";
import { IEnvironmentDeploymentHistoryService } from "Environments/Client/EnvironmentDeploymentHistoryService";
import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import { EnvironmentDeploymentsHistoryActionsCreator } from "Environments/EnvironmentDetails/ActionsCreators/EnvironmentDeploymentsHistoryActionsCreator";
import { Action, Area, FeatureNames } from "Environments/EnvironmentDetails/Common/Utils";
import { EnvironmentDeploymentsHistoryComponent } from "Environments/EnvironmentDetails/Components/EnvironmentDeploymentsHistoryComponent";
import { EnvironmentResourcesComponent } from "Environments/EnvironmentDetails/Components/EnvironmentResourcesComponent";
import { EnvironmentDeploymentsHistoryStore } from "Environments/EnvironmentDetails/Stores/EnvironmentDeploymentsHistoryStore";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { localeFormat } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { getAssetLocation } from "VSS/Platform/Location";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MenuItemType } from "VSSUI/Menu";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from 'VSSUI/Spinner';
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, Tab, TabBar, TabContent } from "VSSUI/Tabs";
import { Toast } from "VSSUI/Toast";
import { Filter } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { ChecksIssuesMessageComponent } from "Checks/ChecksOverview/ChecksIssuesMessageComponent";
import { FeatureFlagConstants, ResourceTypeNames } from "Checks/ChecksOverview/Generated/Constants";

interface IState extends IEnvironmentBaseComponentState {
    filterBarIconVisibility: boolean;
    deletedResourceName?: string;
    selectedTab: string;
    resourceFilterApplied: boolean;
}

class HistoryStateKeys {
    public static View = "view";
    public static DeletedResource = "deletedResource";
}

const LoadingComponent = () => (
    <Spinner
        label={Resources.Loading}
        size={SpinnerSize.large}
        className="absolute-fill"
    />
);

export class EnvironmentComponent extends EnvironmentBaseComponent<IEnvironmentBaseComponentProperties, IState> {

    public getInstanceId(context: IVssComponentContext): string {
        return "environment-instance-id-" + this.componentId;
    }

    public constructor(props: IEnvironmentBaseComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._showCheckIssueMessageCard = isFeatureFlagEnabled(context.pageContext, FeatureFlagConstants.EnableChecksScalabilityResourceIssues, false);

        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._environmentId = getCurrentEnvironmentIdFromRoute(context.pageContext);


        this._environmentActionsCreator = ActionCreatorManager.GetActionCreator<EnvironmentActionsCreator>(EnvironmentActionsCreator, this._instanceId);
        this._environmentDeploymentsHistoryActionsCreator = ActionCreatorManager.CreateActionCreator<EnvironmentDeploymentsHistoryActionsCreator>(EnvironmentDeploymentsHistoryActionsCreator, this._instanceId);

        this._selectedTabId.subscribe(this._onViewStateChanged);
        const selectedTabId = this._getViewFromHistoryState();

        // Initialize environment store 
        const environmentService: IEnvironmentService = context.pageContext.getService<IEnvironmentService>("IEnvironmentService");
        const environment = environmentService.getEnvironmentFromPageData();
        this._environmentStore = StoreManager.CreateStoreWithArgs<EnvironmentStore, IEnvironmentObject>(EnvironmentStore, this._instanceId, environment as IEnvironmentObject);

        // Initialize environment deployment history store
        const historyService = context.pageContext.getService<IEnvironmentDeploymentHistoryService>("IEnvironmentDeploymentHistoryService");
        const environmentDeploymentHistoryResult = historyService.getEnvironmentDeploymentExecutionRecords(this._environmentId);
        this._environmentDeploymentsHistoryStore = StoreManager.CreateStoreWithArgs<EnvironmentDeploymentsHistoryStore, IEnvironmentDeploymentHistoryResult>(EnvironmentDeploymentsHistoryStore, this._instanceId, environmentDeploymentHistoryResult as IEnvironmentDeploymentHistoryResult);

        this._setSelectedTab(selectedTabId);

        this._tabProvider = getTabs(
            context.pageContext,
            "ms.vss-environments-web.environment-tab-group",
            {
                props: {
                    instanceId: this._instanceId,
                    environmentId: this._environmentId,
                    markTTICallback: this._markTTI,
                    filter: this._filter,
                    filterToggled: this._filterToggled,
                    addResourceButtonCallBack: this._addResourceClick,
                    isPageLoad: this._isPageLoad,
                    setFilterBarIconVisibility: this._setFilterBarIconVisibility,
                    resourceFilterApplied: this._setResourceFilterApplied
                }
            }
        );

        const deletedResourceName = this._getDeletedResourceNameFromUrl();
        this.state = {
            errorMessages: {},
            filterBarIconVisibility: true,
            deletedResourceName: deletedResourceName,
            selectedTab: this._selectedTabId.value,
            resourceFilterApplied: false
        };

        const historyState = this._historyService.getState();
        if (historyState.createNew && parseInt(historyState[UrlParamKeys.CreateNew] as string) > 0) {
            this._isAddResourcePanelOpen.value = true;
        }

        this._environmentStore.addChangedListener(this._onEnvironmentStoreChange);
    }

    public render(): JSX.Element {

        const environment = this._environmentStore.getEnvironment();
        const name = environment ? environment.name : "";
        const description = environment ? environment.description : "";

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow" selectedTabId={this._selectedTabId} tabProviders={this._tabProvider}>
                    <Header
                        title={name}
                        backButtonProps={{ onClick: this._onBackButtonClick }}
                        description={description}
                        titleSize={TitleSize.Large}
                        commandBarItems={this._getCommandBarItems()}
                    />
                    { this._getCheckIssueCardComponent(environment) }
                    { this._getTabComponent() }
                    {this._getEditEnvironmentPanelComponent()}
                    {this._getAddResourcePanelComponent()}
                </Page>
                {
                    this.state.deletedResourceName &&
                    <WrappedComponent
                        wrappedType="toast"
                        dependencies={["ms.vss-features.ui-layer-content"]}
                        wrappedRef={this._deletedResourceToastRef}
                        message={localeFormat(Resources.ResourceDeletedToastMessage, this.state.deletedResourceName)}
                    />
                }

            </Surface>
        );
    }

    private _getCheckIssueCardComponent(environment: IEnvironmentObject): React.ReactNode {
        if (this._showCheckIssueMessageCard){
            return <ChecksIssuesMessageComponent resourceTypeName={ResourceTypeNames.Environment} resourceIds={environment.id.toString()} />;
        }
    }


    private _getTabComponent(): JSX.Element {
        const hasResources = this._environmentStore.getResources() && this._environmentStore.getResources().length > 0;
        const hasDeployments = this._environmentDeploymentsHistoryStore.getDeploymentRecords() && 
            this._environmentDeploymentsHistoryStore.getDeploymentRecords().length > 0;
        
        let tabContent = null;

        if (this.state.selectedTab === EnvironmentTabKeys.EnvironmentDeploymentsTabKey) {
            
            tabContent = hasDeployments? (
                <EnvironmentDeploymentsHistoryComponent 
                    instanceId = {this._instanceId}
                    environmentId = {this._environmentId}
                    markTTICallback = {this._markTTI}
                    filter = {this._filter}
                    filterToggled = {this._filterToggled}
                    addResourceButtonCallBack = {this._addResourceClick}
                    isPageLoad = {this._isPageLoad}
                    setFilterBarIconVisibility = {this._setFilterBarIconVisibility}
                    resourceFilterApplied = {this._setResourceFilterApplied}/>
                ): this._getZeroDataContent();
        }

        if(this.state.selectedTab === EnvironmentTabKeys.EnvironmentResourcesTabKey) {
            tabContent = hasResources || this.state.resourceFilterApplied? (
                <EnvironmentResourcesComponent
                    instanceId = {this._instanceId}
                    environmentId = {this._environmentId}
                    markTTICallback = {this._markTTI}
                    filter = {this._filter}
                    filterToggled = {this._filterToggled}
                    addResourceButtonCallBack = {this._addResourceClick}
                    isPageLoad = {this._isPageLoad}
                    setFilterBarIconVisibility = {this._setFilterBarIconVisibility}
                    resourceFilterApplied = {this._setResourceFilterApplied}/>
                ): this._getZeroDataContent();
        }

        if(this.state.selectedTab === EnvironmentTabKeys.EnvironmentChecksTabKey) {
            tabContent = <EnvironmentChecksComponent/>;
        }

        return (
            <>
                <TabBar onSelectedTabChanged={this._onSelectedTabChanged} renderAdditionalContent={this._renderTabBarCommands} >
                    <Tab id={EnvironmentTabKeys.EnvironmentDeploymentsTabKey} key = {EnvironmentTabKeys.EnvironmentDeploymentsTabKey} name={Resources.Deployments} ariaLabel={Resources.Deployments}/>
                    { 
                        hasResources || this.state.resourceFilterApplied ?
                        <Tab id={EnvironmentTabKeys.EnvironmentResourcesTabKey} key = {EnvironmentTabKeys.EnvironmentResourcesTabKey}
                            name={Resources.Resources} ariaLabel={Resources.Resources}/>: null
                    }
                    <Tab id={EnvironmentTabKeys.EnvironmentChecksTabKey} key = {EnvironmentTabKeys.EnvironmentChecksTabKey}
                            name={Resources.Checks} ariaLabel={Resources.Checks}/>
                </TabBar>
                {this._getErrorMessage()}
                <TabContent>
                    { tabContent }
                </TabContent>
            </>
        )
    }

    private _setSelectedTab(selectedTabId: string): void {
       this._selectedTabId.value = selectedTabId || EnvironmentTabKeys.EnvironmentDeploymentsTabKey;
    }

    private _setResourceFilterApplied =  (resourceFilterApplied: boolean) => {
        this.setState({
            resourceFilterApplied: resourceFilterApplied
        });
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._selectedTabId.subscribe(this._onViewStateChanged);
        this._addBreadcrumbs(this._environmentStore.getEnvironment());
    }

    public componentWillUnmount(): void {
        this._environmentStore.removeChangedListener(this._onEnvironmentStoreChange);
        this._selectedTabId.unsubscribe(this._onViewStateChanged);
        StoreManager.DeleteStore<EnvironmentDeploymentsHistoryStore>(EnvironmentDeploymentsHistoryStore, this._instanceId);
        ActionCreatorManager.DeleteActionCreator<EnvironmentDeploymentsHistoryActionsCreator>(EnvironmentDeploymentsHistoryActionsCreator, this._instanceId);
        StoreManager.DeleteStore<EnvironmentStore>(EnvironmentStore, this._instanceId);
        ActionCreatorManager.DeleteActionCreator<EnvironmentActionsCreator>(EnvironmentActionsCreator, this._instanceId);
        super.componentWillUnmount();
    }

    private _getZeroDataContent(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environment-details/Images/no-resources.svg");
        return (
            <div className="zero-data-container">
                <ZeroData
                    primaryText={Resources.ZeroDataResourcesPrimaryText}
                    imagePath={image}
                    imageAltText={Resources.ZeroDataResourcesImageAltText}
                    secondaryText={this._getZeroDataSecondaryText()}
                    actionText={Resources.AddResource}
                    actionType={ZeroDataActionType.ctaButton}
                    onActionClick={this._addResourceClick}
                    className={"zero-data-content"}
                />
            </div>
        )
    }

    private _getZeroDataSecondaryText(): JSX.Element {
        let environmentName = this._environmentStore.getEnvironment().name;
        return (
            <>
                <div className="flex-column flex-center">
                    {Resources.ZeroDataResourcesSecondaryTextEnvironmentReady}
                    <span className="monospaced-text zero-data-yaml-snippet">{`environment: ${environmentName}`}</span>
                    <span className="zero-data-text-spacer">{Resources.ZeroDataResourcesSecondaryTextAddResource}</span>
                </div>
            </>
        );
    }

    private _onViewStateChanged = (selectedPivot: string) => {
        this.setState({
            selectedTab: selectedPivot
        });
    }

    private _availableTabContent(): JSX.Element | undefined {
        if (this.state.selectedTab === EnvironmentTabKeys.EnvironmentDeploymentsTabKey) {
        return (
            <EnvironmentDeploymentsHistoryComponent 
                instanceId = {this._instanceId}
                environmentId = {this._environmentId}
                markTTICallback = {this._markTTI}
                filter = {this._filter}
                filterToggled = {this._filterToggled}
                addResourceButtonCallBack = {this._addResourceClick}
                isPageLoad = {this._isPageLoad}
                setFilterBarIconVisibility = {this._setFilterBarIconVisibility}
                resourceFilterApplied = {this._setResourceFilterApplied}/>
            );
        } else {
            return (
                <EnvironmentResourcesComponent 
                    instanceId = {this._instanceId}
                    environmentId = {this._environmentId}
                    markTTICallback = {this._markTTI}
                    filter = {this._filter}
                    filterToggled = {this._filterToggled}
                    addResourceButtonCallBack = {this._addResourceClick}
                    isPageLoad = {this._isPageLoad}
                    setFilterBarIconVisibility = {this._setFilterBarIconVisibility}
                    resourceFilterApplied = {this._setResourceFilterApplied}/>
                );
        }
    }

    private _setFilterBarIconVisibility = (visible: boolean): void => {
        this.setState({ filterBarIconVisibility: visible });
    }

    private _getErrorMessage(): React.ReactNode {
        if (this._messagesStore.getMessage(PageKey.EnvironmentView)) {
            return (
                <div className="page-content page-content-top">
                    {this.getErrorMessageView(PageKey.EnvironmentView)}
                </div>
            )
        }
    }

    private _renderTabBarCommands = (): JSX.Element | undefined => {
        if (this.state.filterBarIconVisibility) {
            return (<HeaderCommandBarWithFilter filter={this._filter} filterToggled={this._filterToggled} items={[]} />);
        }
    };

    private _getEditEnvironmentPanelComponent(): JSX.Element | undefined {
        return (
            <ConditionalChildren renderChildren={this._isEditEnvironmentPanelOpen}>
                <WrappedComponent
                    dependencies={["ms.vss-environments-web.environments-create-panel"]}
                    wrappedType={"editEnvironment"}
                    instanceId={this._instanceId}
                    onEnvironmentUpdated={this._onEnvironmentUpdated}
                    onDismissPanel={this._onDismissEditEnvironmentPanel}
                    loadingComponent={LoadingComponent}
                    environment={this._environmentStore.getEnvironment()}
                />
            </ConditionalChildren>
        );
    }

    private _getAddResourcePanelComponent(): JSX.Element | undefined {
        return (
            <ConditionalChildren renderChildren={this._isAddResourcePanelOpen}>
                <WrappedComponent
                    dependencies={["ms.vss-environments-web.environments-create-panel"]}
                    wrappedType={"addResources"}
                    instanceId={this._instanceId}
                    onResourceCreated={this._onResourceCreated}
                    onDismissPanel={this._onDismissResourcesPanel}
                    loadingComponent={LoadingComponent}
                    environmentId={this._environmentId}
                    environmentName={this._environmentStore.getEnvironment() && this._environmentStore.getEnvironment().name}
                />
            </ConditionalChildren>
        );
    }

    private _onEnvironmentStoreChange = (): void => {
        const historyState = this._historyService.getState();
        if (!(historyState.createNew && parseInt(historyState[UrlParamKeys.CreateNew] as string) > 0)) {
            this._onDismissResourcesPanel();
        }

        this._onDismissEditEnvironmentPanel();

        this._addBreadcrumbs(this._environmentStore.getEnvironment());

        this.setState({});
    }

    private _addBreadcrumbs(environment: IEnvironmentObject): void {
        if (environment) {
            const { pageContext } = this.context;
            const environmentPageUrl = getEnvironmentPageUrl(pageContext, environment.id);
            addBreadcrumbs(pageContext, { name: environment.name, url: environmentPageUrl });
        }
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        if (!this._environmentStore.getEnvironment()) {
            return [];
        }

        let commandBarItems = [
            {
                text: Resources.AddResource,
                ariaLabel: Resources.AddResource,
                id: "add-resource",
                isPrimary: true,
                onActivate: this._addResourceClick,
                important: true,
                rank: 1
            } as IHeaderCommandBarItem,
            {
                text: Resources.EditButtonText,
                ariaLabel: Resources.EditButtonText,
                id: "edit-environment",
                isPrimary: false,
                onActivate: this._editButttonClick,
                important: false,
                rank: 2,
                iconProps: { iconName: "Edit" }
            } as IHeaderCommandBarItem,
            {
                text: Resources.Security,
                ariaLabel: Resources.Security,
                id: "environment-security",
                isPrimary: false,
                onActivate: () => {
                    onClickFPS(this.context.pageContext, getEnvironmentsSecurityPageUrl(this.context.pageContext, this._environmentId), true);
                },
                important: false,
                rank: 3,
                iconProps: { iconName: "Lock" }
            } as IHeaderCommandBarItem,
        ];

        commandBarItems.push(
            {
                id: "menu-divider-1",
                itemType: MenuItemType.Divider,
                important: false,
                rank: 5
            } as IHeaderCommandBarItem,
            {
                text: Resources.Delete,
                ariaLabel: Resources.Delete,
                id: "delete-environment",
                isPrimary: false,
                onActivate: this._onDeleteEnvironmentClicked,
                important: false,
                rank: 6,
                iconProps: { iconName: "Delete" }
            }
        );
        return commandBarItems;
    }

    private _onBackButtonClick = (): void => {
        onClickFPS(this.context.pageContext, getEnvironmentsPageUrl(this.context.pageContext), true);
    }

    private _addResourceClick = (): void => {
        this._isAddResourcePanelOpen.value = true;
    }

    private _editButttonClick = (): void => {
        this._isEditEnvironmentPanelOpen.value = true;
    }

    private _onDeleteEnvironmentClicked = (): void => {
        const environment = this._environmentStore.getEnvironment();
        const dialogProps: IDeleteDialogProps = {
            id: environment.id,
            headerText: localeFormat(Resources.DeleteDialogHeader, environment.name),
            message: Resources.DeleteEnvironmentDialogText,
            onOkClick: this._onDeleteEnvironmentDialogOkClick,
            onDismiss: () => null
        }

        getDeleteDialog(this.context.pageContext, dialogProps);
    }

    private _onDeleteEnvironmentDialogOkClick = (): void => {
        publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.EnvironmentDeleted, "EnvironmentId": this._environmentId.toString()});
        this._environmentActionsCreator.deleteEnvironment(this.context.pageContext, this._environmentId);
    }

    private _onDismissResourcesPanel = (): void => {
        this._isAddResourcePanelOpen.value = false;
    }

    private _onResourceCreated = (): void => {
        this._environmentActionsCreator.getResourcesWithFilters(this.context.pageContext, undefined, undefined, undefined, true, true, true);
    }

    private _onDismissEditEnvironmentPanel = (): void => {
        this._isEditEnvironmentPanelOpen.value = false;
    }

    private _onEnvironmentUpdated = (environment: EnvironmentInstance): void => {
        publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.EnvironmentEdited, "EnvironmentId": environment.id.toString()});
        this._environmentActionsCreator.updateEnvironment(environment);
    }

    private _getViewFromHistoryState(): string {
        const state = this._historyService.getState();

        return !!state ? state[HistoryStateKeys.View] : state;
    }

    private _updateHistoryState(): void {
        const state = this._historyService.getState() || {};
        state[HistoryStateKeys.View] = this._selectedTabId.value;

        this._historyService.replaceState(state);
    }

    private _onSelectedTabChanged = (tabId: string): void => {
        if (!tabId) {
            tabId = EnvironmentTabKeys.EnvironmentResourcesTabKey;
        }

        this._isPageLoadScenario = false;

        this._selectedTabId.value = tabId;

        
        this._setFilterBarIconVisibility(tabId !==  EnvironmentTabKeys.EnvironmentChecksTabKey);
        

        this._updateHistoryState();
    }

    private _isPageLoad = (): boolean => {
        return this._isPageLoadScenario;
    }

    private _markTTI = (scenarioName: string): void => {
        if (!this._isTTIMarked && this._isPageLoadScenario) {
            this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive("Environments", scenarioName);
            this._isTTIMarked = true;
        }
    }

    private _getDeletedResourceNameFromUrl(): string | undefined {
        const historyState = this._historyService.getState();
        const deletedResourceName = historyState[HistoryStateKeys.DeletedResource];
        if (deletedResourceName) {
            delete historyState[HistoryStateKeys.DeletedResource];
            this._historyService.replaceState(historyState);

            this.setTimeout(
                () => {
                    if (this._deletedResourceToastRef && this._deletedResourceToastRef.current) {
                        this._deletedResourceToastRef.current.fadeOut().promise.then(() => {
                            this.setState({
                                deletedResourceName: undefined
                            });
                        });
                    }
                },
                15 * 1000 /*15 seconds*/);
        }

        return deletedResourceName;
    }

    private _environmentStore: EnvironmentStore;
    private _environmentActionsCreator: EnvironmentActionsCreator;
    private _environmentDeploymentsHistoryActionsCreator: EnvironmentDeploymentsHistoryActionsCreator;
    private _environmentDeploymentsHistoryStore: EnvironmentDeploymentsHistoryStore;
    private _tabProvider: IReadonlyObservableArray<IVssContributedTab>;
    private _isTTIMarked: boolean = false;
    private _isPageLoadScenario: boolean = true;
    private readonly _environmentId: number;
    private readonly _selectedTabId = new ObservableValue<string>(EnvironmentTabKeys.EnvironmentResourcesTabKey);
    private readonly _isEditEnvironmentPanelOpen = new ObservableValue<boolean>(false);
    private readonly _isAddResourcePanelOpen = new ObservableValue<boolean>(false);
    private readonly _filter = new Filter();
    private readonly _filterToggled = new ObservableValue<boolean>(false);
    private readonly _historyService: IVssHistoryService;
    private _deletedResourceToastRef: React.RefObject<Toast> = React.createRef<Toast>();
    private _showCheckIssueMessageCard: boolean;
}

VssComponent.register("environmentComponent", EnvironmentComponent);
